import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import { BACKEND_URL } from '../config';
import {
  PaperPlaneTilt,
  CheckCircle,
  XCircle,
  Plus,
  Trash,
  Info,
  TelegramLogo,
} from '@phosphor-icons/react';

const TelegramSettings = () => {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [botToken, setBotToken] = useState('');
  const [chatIds, setChatIds] = useState(['']);
  const [enabled, setEnabled] = useState(true);
  const [autoSend, setAutoSend] = useState(false);
  const [testResults, setTestResults] = useState(null);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/telegram/settings`, { withCredentials: true });
      setSettings(data);
      if (data.configured) {
        setChatIds(data.chat_ids?.length ? data.chat_ids : ['']);
        setEnabled(data.enabled);
        setAutoSend(data.auto_send_reports);
      }
    } catch (err) { console.error(err.message);
      // Not configured yet
    } finally {
      setLoading(false);
    }
  };

  const handleAddChatId = () => setChatIds([...chatIds, '']);

  const handleRemoveChatId = (index) => {
    const updated = chatIds.filter((_, i) => i !== index);
    setChatIds(updated.length ? updated : ['']);
  };

  const handleChatIdChange = (index, value) => {
    const updated = [...chatIds];
    updated[index] = value;
    setChatIds(updated);
  };

  const handleSave = async () => {
    if (!botToken && !settings?.configured) {
      toast.error('Please enter your Telegram bot token');
      return;
    }
    const validIds = chatIds.filter(c => c.trim());
    if (!validIds.length) {
      toast.error('Please add at least one Chat ID');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        bot_token: botToken || '___KEEP_EXISTING___',
        chat_ids: validIds,
        enabled,
        auto_send_reports: autoSend,
      };
      if (!botToken && settings?.configured) {
        payload.bot_token = '___KEEP_EXISTING___';
      }
      const { data } = await axios.post(`${BACKEND_URL}/api/telegram/settings`, payload, { withCredentials: true });
      if (data.bot_verified) {
        toast.success(`Telegram connected! Bot: @${data.bot_name}`);
      } else {
        toast.error(`Settings saved but bot verification failed: ${data.error || 'Check your token'}`);
      }
      setBotToken('');
      fetchSettings();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    setTesting(true);
    setTestResults(null);
    try {
      const { data } = await axios.post(`${BACKEND_URL}/api/telegram/test`, {}, { withCredentials: true });
      setTestResults(data);
      if (data.results?.every(r => r.success)) {
        toast.success('Test messages sent successfully!');
      } else {
        toast.error('Some test messages failed. Check results below.');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Test failed');
    } finally {
      setTesting(false);
    }
  };

  if (loading) return null;

  return (
    <div className="space-y-5" data-testid="telegram-settings">
      <div className="flex items-center gap-2 mb-1">
        <TelegramLogo size={22} weight="fill" className="text-blue-500" />
        <span className="text-sm font-semibold">Telegram Report Delivery</span>
      </div>
      <p className="text-xs text-muted-foreground leading-relaxed">
        Connect a Telegram bot to receive FY and Quarterly reports directly in your Telegram chats, groups, or channels.
        Completely free, no limits, no verification required.
      </p>

      {settings?.configured && (
        <div className="flex items-center gap-2 text-xs">
          <CheckCircle size={14} weight="fill" className="text-green-500" />
          <span className="text-muted-foreground">
            Connected{settings.bot_name ? ` (@${settings.bot_name})` : ''} &middot; {settings.chat_ids?.length || 0} chat{settings.chat_ids?.length !== 1 ? 's' : ''}
          </span>
        </div>
      )}

      <div className="space-y-3">
        <div className="space-y-1.5">
          <Label className="text-xs tracking-[0.15em] uppercase font-bold text-muted-foreground">
            Bot Token {settings?.configured && <span className="normal-case tracking-normal font-normal">(leave blank to keep existing)</span>}
          </Label>
          <Input
            type="password"
            value={botToken}
            onChange={(e) => setBotToken(e.target.value)}
            placeholder={settings?.configured ? settings.bot_token_masked : 'e.g. 123456789:ABCdefGhIjKlMnOpQrStUvWxYz'}
            className="rounded-sm font-mono text-xs"
            data-testid="tg-bot-token-input"
          />
        </div>

        <div className="space-y-1.5">
          <Label className="text-xs tracking-[0.15em] uppercase font-bold text-muted-foreground">Chat IDs</Label>
          <p className="text-[11px] text-muted-foreground">
            User, group, or channel IDs where reports will be sent. Use <span className="font-mono">@userinfobot</span> on Telegram to find your ID.
          </p>
          {chatIds.map((cid, idx) => (
            <div key={idx} className="flex items-center gap-2">
              <Input
                value={cid}
                onChange={(e) => handleChatIdChange(idx, e.target.value)}
                placeholder="e.g. 123456789 or -1001234567890"
                className="rounded-sm font-mono text-xs flex-1"
                data-testid={`tg-chat-id-${idx}`}
              />
              {chatIds.length > 1 && (
                <button onClick={() => handleRemoveChatId(idx)} className="p-1.5 hover:bg-destructive/10 text-destructive rounded-sm">
                  <Trash size={14} />
                </button>
              )}
            </div>
          ))}
          <Button variant="ghost" size="sm" className="text-xs h-7 px-2" onClick={handleAddChatId}>
            <Plus size={12} className="mr-1" /> Add Chat ID
          </Button>
        </div>

        <div className="flex items-center justify-between pt-2">
          <Label className="text-sm">Enable Telegram Delivery</Label>
          <Switch checked={enabled} onCheckedChange={setEnabled} data-testid="tg-enabled-toggle" />
        </div>

        <div className="flex items-center justify-between">
          <div>
            <Label className="text-sm">Auto-send Scheduled Reports</Label>
            <p className="text-[11px] text-muted-foreground mt-0.5">FY & Quarterly reports sent automatically</p>
          </div>
          <Switch checked={autoSend} onCheckedChange={setAutoSend} data-testid="tg-auto-send-toggle" />
        </div>
      </div>

      <div className="flex gap-2 pt-2">
        <Button onClick={handleSave} disabled={saving} className="rounded-sm flex-1" data-testid="tg-save-button">
          {saving ? 'Saving...' : 'Save Telegram Settings'}
        </Button>
        {settings?.configured && (
          <Button onClick={handleTest} disabled={testing} variant="outline" className="rounded-sm" data-testid="tg-test-button">
            <PaperPlaneTilt size={14} className="mr-1.5" />
            {testing ? 'Sending...' : 'Test'}
          </Button>
        )}
      </div>

      {testResults && (
        <div className="space-y-1 pt-1">
          {testResults.results?.map((r, i) => (
            <div key={i} className="flex items-center gap-2 text-xs">
              {r.success ? (
                <CheckCircle size={14} weight="fill" className="text-green-500" />
              ) : (
                <XCircle size={14} weight="fill" className="text-destructive" />
              )}
              <span className="font-mono">{r.chat_id}</span>
              {r.error && <span className="text-destructive">{r.error}</span>}
            </div>
          ))}
        </div>
      )}

      <div className="bg-muted/50 rounded-sm p-3 flex gap-2">
        <Info size={14} className="text-muted-foreground shrink-0 mt-0.5" />
        <div className="text-[11px] text-muted-foreground leading-relaxed">
          <strong>Quick Setup:</strong>
          <ol className="list-decimal ml-3.5 mt-1 space-y-0.5">
            <li>Message <span className="font-mono font-bold">@BotFather</span> on Telegram &rarr; type <span className="font-mono">/newbot</span></li>
            <li>Follow the prompts to name your bot &rarr; copy the <strong>bot token</strong></li>
            <li>Add the bot to your group/channel (or message it directly)</li>
            <li>Get your Chat ID via <span className="font-mono font-bold">@userinfobot</span> or forward a message to <span className="font-mono font-bold">@JsonDumpBot</span></li>
            <li>Paste the token and chat IDs above, save, and test!</li>
          </ol>
        </div>
      </div>
    </div>
  );
};

export default TelegramSettings;
